import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, post, patch, del } from '@/lib/api'

import type { PaginationMeta, LeaveType, LeaveStatus } from '@company/shared'


// ==================== Types ====================

export type { LeaveType, LeaveStatus }

export interface Leave {
  id: string
  employeeId: string
  type: LeaveType
  startDate: string
  endDate: string
  days: number
  reason: string
  status: LeaveStatus
  approverId: string | null
  approvedAt: string | null
  rejectedReason: string | null
  createdAt: string
  updatedAt: string | null
  deletedAt: string | null
}

export interface LeaveWithDetails extends Leave {
  employee?: {
    id: string
    name: string
    employeeNo: string
  } | null
  approver?: {
    id: string
    name: string
  } | null
}

export interface LeaveBalance {
  type: LeaveType
  total: number
  used: number
  remaining: number
}

export interface LeaveStatistics {
  byType: Array<{ type: LeaveType; count: number; days: number }>
  byMonth: Array<{ month: string; count: number; days: number }>
  byStatus: Array<{ status: LeaveStatus; count: number }>
}

export interface LeaveListParams {
  employeeId?: string
  type?: LeaveType
  status?: LeaveStatus
  startDate?: string
  endDate?: string
  page?: number
  pageSize?: number
}

export interface CreateLeaveInput {
  employeeId?: string
  type: LeaveType
  startDate: string
  endDate: string
  reason: string
  submit?: boolean
}

export interface UpdateLeaveInput {
  type?: LeaveType
  startDate?: string
  endDate?: string
  reason?: string
  submit?: boolean
}

// ==================== Query Keys ====================

export const leaveKeys = {
  all: ['leaves'] as const,
  lists: () => [...leaveKeys.all, 'list'] as const,
  list: (params: LeaveListParams) => [...leaveKeys.lists(), params] as const,
  pending: () => [...leaveKeys.all, 'pending'] as const,
  details: () => [...leaveKeys.all, 'detail'] as const,
  detail: (id: string) => [...leaveKeys.details(), id] as const,
  balance: (employeeId?: string, year?: number) => [...leaveKeys.all, 'balance', employeeId, year] as const,
  statistics: (params?: { employeeId?: string; year?: number }) => [...leaveKeys.all, 'statistics', params] as const,
}

// ==================== Hooks ====================

/**
 * 获取请假列表
 */
export function useLeaves(params: LeaveListParams = {}) {
  return useQuery({
    queryKey: leaveKeys.list(params),
    queryFn: () =>
      get<{ data: Leave[]; meta: PaginationMeta }>('/hr/leaves', params as Record<string, unknown>),
  })
}

/**
 * 获取待审批列表
 */
export function usePendingLeaves() {
  return useQuery({
    queryKey: leaveKeys.pending(),
    queryFn: () => get<Leave[]>('/hr/leaves/pending'),
  })
}

/**
 * 获取单个请假详情
 */
export function useLeave(id: string) {
  return useQuery({
    queryKey: leaveKeys.detail(id),
    queryFn: () => get<LeaveWithDetails>(`/hr/leaves/${id}`),
    enabled: !!id,
  })
}

/**
 * 获取我的假期余额
 */
export function useMyLeaveBalance(year?: number) {
  return useQuery({
    queryKey: leaveKeys.balance(undefined, year),
    queryFn: () => get<LeaveBalance[]>('/hr/leaves/balance', year ? { year } : {}),
  })
}

/**
 * 获取员工假期余额
 */
export function useEmployeeLeaveBalance(employeeId: string, year?: number) {
  return useQuery({
    queryKey: leaveKeys.balance(employeeId, year),
    queryFn: () => get<LeaveBalance[]>(`/hr/leaves/balance/${employeeId}`, year ? { year } : {}),
    enabled: !!employeeId,
  })
}

/**
 * 获取请假统计
 */
export function useLeaveStatistics(params?: { employeeId?: string; year?: number }) {
  return useQuery({
    queryKey: leaveKeys.statistics(params),
    queryFn: () => get<LeaveStatistics>('/hr/leaves/statistics', params as Record<string, unknown>),
  })
}

/**
 * 创建请假申请
 */
export function useCreateLeave() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateLeaveInput) => post<Leave>('/hr/leaves', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: leaveKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: leaveKeys.pending() })
    },
  })
}

/**
 * 更新请假申请
 */
export function useUpdateLeave() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLeaveInput }) =>
      patch<Leave>(`/hr/leaves/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: leaveKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: leaveKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: leaveKeys.pending() })
    },
  })
}

/**
 * 取消请假申请
 */
export function useCancelLeave() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => del<Leave>(`/hr/leaves/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: leaveKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: leaveKeys.pending() })
    },
  })
}

/**
 * 审批通过
 */
export function useApproveLeave() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => post<Leave>(`/hr/leaves/${id}/approve`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: leaveKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: leaveKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: leaveKeys.pending() })
      void queryClient.invalidateQueries({ queryKey: leaveKeys.balance() })
    },
  })
}

/**
 * 审批拒绝
 */
export function useRejectLeave() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      post<Leave>(`/hr/leaves/${id}/reject`, { reason }),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: leaveKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: leaveKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: leaveKeys.pending() })
    },
  })
}

/**
 * 撤销已批准的请假
 */
export function useRevokeLeave() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => post<Leave>(`/hr/leaves/${id}/revoke`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: leaveKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: leaveKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: leaveKeys.balance() })
    },
  })
}

/**
 * 设置假期余额
 */
export function useSetLeaveBalance() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { employeeId: string; year: number; type: LeaveType; total: number }) =>
      post<{ id: string }>('/hr/leaves/balance', data),
    onSuccess: (_, { employeeId, year }) => {
      void queryClient.invalidateQueries({ queryKey: leaveKeys.balance(employeeId, year) })
    },
  })
}

// ==================== Batch Approval Types ====================

export interface LeaveBatchApprovalResult {
  success: string[]
  failed: Array<{ id: string; reason: string }>
}

/**
 * 批量审批通过
 */
export function useBatchApproveLeaves() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) =>
      post<LeaveBatchApprovalResult>('/hr/leaves/batch-approve', { ids }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: leaveKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: leaveKeys.pending() })
      void queryClient.invalidateQueries({ queryKey: leaveKeys.balance() })
    },
  })
}

/**
 * 批量审批拒绝
 */
export function useBatchRejectLeaves() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ ids, reason }: { ids: string[]; reason: string }) =>
      post<LeaveBatchApprovalResult>('/hr/leaves/batch-reject', { ids, reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: leaveKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: leaveKeys.pending() })
    },
  })
}
